import { Link } from 'react-router-dom';
import {
  CircuitBoardSVG,
  NeuralNetSVG,
  IoTNodesSVG,
  SmartBinSVG,
  SensorChipSVG,
  RoboticArmSVG,
} from '../components/TechIllustrations';

const services = [
  { icon: '📡', title: 'IoT Solutions', desc: 'Smart agriculture, connected buildings, energy monitoring & emergency safety networks.', color: 'green', to: '/services' },
  { icon: '🔌', title: 'Electronics & Embedded', desc: 'Custom PCB design, prototyping, firmware and affordable electronics repair.', color: 'red', to: '/services' },
  { icon: '🦾', title: 'Robotics', desc: 'Educational kits, industrial automation and service robots for urban environments.', color: 'green', to: '/services' },
  { icon: '🧠', title: 'AI & Machine Learning', desc: 'Neural networks, computer vision, predictive analytics and edge AI deployment.', color: 'red', to: '/services' },
  { icon: '🎓', title: 'Training & Capacity', desc: 'Youth programs, professional certification, hackathons and innovation challenges.', color: 'green', to: '/services' },
  { icon: '🛒', title: 'E-commerce', desc: 'Robotics kits, IoT devices, sensors and training packages with secure delivery.', color: 'red', to: '/services' },
];

const HomePage = () => (
  <div className="bg-white">

    {/* ═══════════ HERO ═══════════ */}
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20 lg:py-28">
      {/* dot grid */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '30px 30px' }} />
      <div className="pointer-events-none absolute -top-40 -left-40 h-96 w-96 rounded-full bg-[#1aa534]/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-[#d2252e]/20 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">

          {/* Text */}
          <div className="space-y-8 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2">
              <span className="h-2 w-2 rounded-full bg-[#1aa534] animate-pulse" />
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-300">
                IoT · Robotics · AI · Electronics
              </span>
            </div>
            <div>
              <h1 className="text-5xl font-bold leading-tight text-white sm:text-6xl">
                <span className="text-[#d2252e]">BETR</span>obionics
              </h1>
              <p className="mt-2 text-lg font-medium text-slate-300">
                Burundi Emergency Technology Group
              </p>
              <p className="mt-5 max-w-lg text-base leading-8 text-slate-400">
                We design and deploy <strong className="text-white">smart systems</strong> that
                connect people, devices, and environments across Africa.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/services" className="btn-primary-lg">Explore Our Services</Link>
              <Link to="/contact" className="btn-outline-lg !border-white/20 !text-slate-300 hover:!border-[#1aa534] hover:!text-white">
                Get a Quote
              </Link>
            </div>
            <div className="grid grid-cols-4 gap-4 border-t border-white/10 pt-6">
              {[
                { v: '6', l: 'Service Areas' },
                { v: '3', l: 'Core Poles' },
                { v: '100%', l: 'Local Innovation' },
                { v: '🌍', l: 'East Africa' },
              ].map(s => (
                <div key={s.l} className="text-center">
                  <p className="text-2xl font-bold text-white">{s.v}</p>
                  <p className="mt-1 text-[10px] text-slate-500 uppercase tracking-wide">{s.l}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Hero illustration: circuit board */}
          <div className="animate-fade-in-up" style={{ animationDelay: '150ms' }}>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
              <p className="mb-3 text-center text-[10px] font-semibold uppercase tracking-widest text-[#1aa534]">
                BETRobionics Hardware Lab
              </p>
              <CircuitBoardSVG className="w-full" />
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ═══════════ SERVICES GRID ═══════════ */}
    <section className="bg-slate-50 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <span className="section-label">What We Do</span>
          <h2 className="mt-4 text-3xl font-bold text-slate-900 sm:text-4xl">Six Areas of Innovation</h2>
          <p className="mt-4 mx-auto max-w-2xl text-slate-500 leading-7">
            From connected sensors to robotic systems, BETRobionics delivers end-to-end technology
            solutions tailored for Burundi and East Africa.
          </p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((svc, i) => (
            <Link key={svc.title} to={svc.to}
              className="card group animate-fade-in-up"
              style={{ animationDelay: `${i * 60}ms` }}>
              <div className={`mb-4 text-3xl`}>{svc.icon}</div>
              <h3 className="text-base font-semibold text-slate-900 group-hover:text-[#1aa534] transition-colors">
                {svc.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-500">{svc.desc}</p>
              <span className={`mt-4 inline-flex items-center gap-1 text-xs font-semibold ${svc.color === 'green' ? 'text-[#1aa534]' : 'text-[#d2252e]'}`}>
                Learn more →
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* ═══════════ NEURAL NETWORK SECTION ═══════════ */}
    <section className="bg-white py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="section-label">AI & Machine Learning</span>
            <h2 className="mt-4 text-3xl font-bold text-slate-900 sm:text-4xl">
              Neural networks built<br />for real-world problems
            </h2>
            <p className="mt-5 text-slate-500 leading-7">
              Our AI team designs deep learning models, trains them on local African datasets,
              and deploys them on edge devices — no cloud dependency required.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                'Computer vision for crop disease detection',
                'Predictive maintenance for industrial machines',
                'Natural language models in local languages',
                'Edge AI on Raspberry Pi & Jetson Nano',
              ].map(item => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#1aa534]" />
                  {item}
                </li>
              ))}
            </ul>
            <Link to="/services" className="btn-primary mt-8 inline-flex">Discover AI Services →</Link>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <p className="mb-3 text-center text-[10px] font-semibold uppercase tracking-widest text-[#d2252e]">
              Deep Learning Architecture
            </p>
            <NeuralNetSVG className="w-full" />
          </div>
        </div>
      </div>
    </section>

    {/* ═══════════ IOT NODES SECTION ═══════════ */}
    <section className="bg-slate-50 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 order-2 lg:order-1">
            <p className="mb-3 text-center text-[10px] font-semibold uppercase tracking-widest text-[#1aa534]">
              IoT Sensor Network Topology
            </p>
            <IoTNodesSVG className="w-full" />
          </div>
          <div className="order-1 lg:order-2">
            <span className="section-label">IoT & Connected Systems</span>
            <h2 className="mt-4 text-3xl font-bold text-slate-900 sm:text-4xl">
              Every device, every sensor,<br />connected in real time
            </h2>
            <p className="mt-5 text-slate-500 leading-7">
              We architect LoRa, WiFi and cellular sensor networks that work in remote
              farms, dense cities, and everything in between — even with limited connectivity.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                { label: 'Smart Agriculture', color: 'green' },
                { label: 'Smart Buildings', color: 'green' },
                { label: 'Energy Metering', color: 'red' },
                { label: 'Disaster Alerts', color: 'red' },
              ].map(({ label, color }) => (
                <div key={label}
                  className={`rounded-xl border p-3 text-sm font-medium ${color === 'green' ? 'border-[#1aa534]/20 bg-[#1aa534]/5 text-[#1aa534]' : 'border-[#d2252e]/20 bg-[#d2252e]/5 text-[#d2252e]'}`}>
                  {label}
                </div>
              ))}
            </div>
            <Link to="/services" className="btn-primary mt-8 inline-flex">View IoT Solutions →</Link>
          </div>
        </div>
      </div>
    </section>

    {/* ═══════════ SMART BIN + CHIP + ARM ═══════════ */}
    <section className="bg-white py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <span className="section-label-red">Technology Showcase</span>
          <h2 className="mt-4 text-3xl font-bold text-slate-900 sm:text-4xl">
            Our products in action
          </h2>
          <p className="mt-4 mx-auto max-w-xl text-slate-500 leading-7">
            From smart waste management to embedded chips and robotic arms — BETRobionics
            builds full hardware systems from scratch.
          </p>
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {/* Smart Bin */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center hover:shadow-md transition-shadow">
            <SmartBinSVG className="mx-auto h-48" />
            <h3 className="mt-4 text-base font-semibold text-slate-900">Smart Waste Bin</h3>
            <p className="mt-2 text-sm text-slate-500 leading-6">
              IoT-connected bins that report fill levels, route collection trucks and
              reduce urban waste overflow in real time.
            </p>
            <span className="mt-3 inline-block rounded-full bg-[#1aa534]/10 px-3 py-1 text-xs font-semibold text-[#1aa534]">
              Smart City
            </span>
          </div>
          {/* Sensor Chip */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center hover:shadow-md transition-shadow">
            <SensorChipSVG className="mx-auto h-48" />
            <h3 className="mt-4 text-base font-semibold text-slate-900">Embedded MCU Design</h3>
            <p className="mt-2 text-sm text-slate-500 leading-6">
              Custom microcontroller boards and PCBs designed, tested and manufactured
              for IoT sensors, robotics and industrial control.
            </p>
            <span className="mt-3 inline-block rounded-full bg-[#d2252e]/10 px-3 py-1 text-xs font-semibold text-[#d2252e]">
              Electronics
            </span>
          </div>
          {/* Robotic Arm */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center hover:shadow-md transition-shadow">
            <RoboticArmSVG className="mx-auto h-48" />
            <h3 className="mt-4 text-base font-semibold text-slate-900">Industrial Robotic Arm</h3>
            <p className="mt-2 text-sm text-slate-500 leading-6">
              Multi-axis robotic arms for manufacturing, packaging and precision
              tasks — programmable, reliable and locally maintained.
            </p>
            <span className="mt-3 inline-block rounded-full bg-[#1aa534]/10 px-3 py-1 text-xs font-semibold text-[#1aa534]">
              Robotics
            </span>
          </div>
        </div>
      </div>
    </section>

    {/* ═══════════ WHY BETR ═══════════ */}
    <section className="bg-slate-50 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="section-label-red">Why BETRobionics</span>
            <h2 className="mt-4 text-3xl font-bold text-slate-900 sm:text-4xl">
              Local expertise,<br />global-quality technology
            </h2>
            <p className="mt-5 text-slate-500 leading-7">
              We combine deep local knowledge with cutting-edge technology to deliver
              solutions that actually work in African conditions — reliable, affordable, and built to last.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { title: 'Designed for Africa', desc: 'Every solution is engineered for local infrastructure, climate, and connectivity realities.' },
                { title: 'Full-Stack Capability', desc: 'From hardware design to cloud dashboards — we handle every layer of your smart system.' },
                { title: 'Community Impact', desc: 'We invest in youth, women, and students through training programs and innovation challenges.' },
                { title: 'Proven Technology', desc: 'Industry-standard IoT protocols, robotics frameworks and AI tools adapted for your use case.' },
              ].map(({ title, desc }) => (
                <div key={title} className="flex items-start gap-3">
                  <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1aa534]/15">
                    <svg className="h-3 w-3 text-[#1aa534]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{title}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { n: '6', label: 'Service Areas', color: 'green' },
              { n: '3', label: 'Core Poles', color: 'red' },
              { n: 'IoT', label: 'Connected Systems', color: 'green' },
              { n: 'AI', label: 'Smart Solutions', color: 'red' },
            ].map(({ n, label, color }) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all">
                <p className={`text-3xl font-bold ${color === 'green' ? 'text-[#1aa534]' : 'text-[#d2252e]'}`}>{n}</p>
                <p className="mt-2 text-xs text-slate-500">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ═══════════ CTA ═══════════ */}
    <section className="bg-[#1aa534] py-16">
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-white sm:text-4xl">Ready to build something smart?</h2>
        <p className="mt-4 text-lg text-green-100 leading-7">
          Whether it's an IoT deployment, a robotics prototype, or a training program —
          BETRobionics is your technology partner in Burundi and East Africa.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/contact"
            className="rounded-lg bg-white px-8 py-3.5 text-base font-semibold text-[#1aa534] shadow-sm hover:bg-slate-50 transition-colors">
            Start a Project
          </Link>
          <a href="tel:+25767706285"
            className="rounded-lg border-2 border-white/40 px-8 py-3.5 text-base font-semibold text-white hover:border-white transition-colors">
            +257 67 70 62 85
          </a>
        </div>
      </div>
    </section>

  </div>
);

export default HomePage;
